import React from 'react';
import { Button, Flex, Spin, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import GscDomainPickerCard from './GscDomainPickerCard';
import GscDomainPickerFooter from './GscDomainPickerFooter';
import GscPropertyPickerList from './GscPropertyPickerList';
import GscReplacePropertyModal from './GscReplacePropertyModal';
import SearchPerformanceDomainDeadEnd from './SearchPerformanceDomainDeadEnd';
import useGscDomainPicker from './useGscDomainPicker';

type Props = {
  sessionId: string;
  sourceId: string;
  lostUrls?: string[];
  isGoingBack?: boolean;
  onBack: () => void;
  onActivated: (selectedUrls: string[]) => void;
};

const STRINGS = {
  cardTitle: (maxDomains: number) =>
    maxDomains === 1
      ? 'Choose the domain to sync with Google Search Console'
      : `Choose up to ${maxDomains} domains to sync with Google Search Console`,
  back: 'Back',
  connect: 'Connect and sync domains',
  tryAgain: 'Try again',
  signInAgain: 'Sign in with Google again',
  useDifferentAccount: 'Use a different Google account',
  panelTitle: 'What happens when you sync',
  panelItems: [
    'You choose which of your domains to include',
    'We match each one to your Search Console property',
    'We build your Search Performance report from both sides',
  ],
};

const UPGRADE_CTA_LOCATION = 'Search Performance Connect - Domain Picker';

function SearchPerformanceDomainStep({ sessionId, sourceId, lostUrls, isGoingBack, onBack, onActivated }: Props) {
  const { token } = theme.useToken();
  const trackEvent = useTrackSearchPerformanceEvent();
  const picker = useGscDomainPicker({ sessionId, sourceId, lostUrls, onActivated });

  const replaceOfferModal = (
    <GscReplacePropertyModal
      conflicts={picker.replaceConflicts}
      confirmLoading={picker.isActivating}
      onConfirm={picker.confirmReplace}
      onCancel={picker.dismissReplaceOffer}
    />
  );

  const backButton = (
    <Button
      type="link"
      icon={<ArrowLeftOutlined />}
      style={{ paddingLeft: 0 }}
      disabled={isGoingBack}
      onClick={() => {
        trackEvent('Search Performance: Domain Step Back Clicked', { selected_count: picker.selected.length });
        onBack();
      }}
    >
      {STRINGS.back}
    </Button>
  );

  if (picker.isFetching) {
    return (
      <>
        <Flex vertical gap={token.marginLG}>
          <Flex justify="center" style={{ padding: token.paddingLG }}>
            <Spin />
          </Flex>
          {backButton}
        </Flex>
        {replaceOfferModal}
      </>
    );
  }

  // Dead ends replace the picker: inside it they sat above a permanently
  // disabled submit and read as a broken form.
  if (picker.deadEndReason) {
    const deadEndReason = picker.deadEndReason;
    return (
      <>
        <Flex vertical gap={token.marginLG}>
          <SearchPerformanceDomainDeadEnd
            reason={deadEndReason}
            unmatchedSiteUrls={picker.session?.unmatchedSiteUrls}
            lostUrls={picker.lostUrlsGoogleNoLongerLists}
          />
          <GscDomainPickerFooter
            back={backButton}
            primary={
              deadEndReason === 'session_load_failed' ? (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    trackEvent('Search Performance: Dead End Action Clicked', {
                      reason: deadEndReason,
                      action: 'retry_load',
                    });
                    picker.refetch();
                  }}
                >
                  {STRINGS.tryAgain}
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  disabled={isGoingBack}
                  onClick={() => {
                    trackEvent('Search Performance: Dead End Action Clicked', {
                      reason: deadEndReason,
                      action: 'reauthenticate',
                    });
                    onBack();
                  }}
                >
                  {deadEndReason === 'session_expired' ? STRINGS.signInAgain : STRINGS.useDifferentAccount}
                </Button>
              )
            }
          />
        </Flex>
        {replaceOfferModal}
      </>
    );
  }

  return (
    <>
      <Flex vertical gap={token.marginLG}>
        <GscDomainPickerCard
          title={STRINGS.cardTitle(picker.maxDomains)}
          panelTitle={STRINGS.panelTitle}
          panelItems={STRINGS.panelItems}
        >
          <GscPropertyPickerList
            picker={picker}
            upgradeCtaLocation={UPGRADE_CTA_LOCATION}
            accountEmail={picker.session?.connectedAccountEmail}
          />
        </GscDomainPickerCard>

        <GscDomainPickerFooter
          back={backButton}
          primary={
            <Button
              type="primary"
              size="large"
              loading={picker.isActivating}
              disabled={picker.selected.length < 1}
              onClick={picker.handleActivate}
            >
              {STRINGS.connect}
            </Button>
          }
        />
      </Flex>
      {replaceOfferModal}
    </>
  );
}

export default SearchPerformanceDomainStep;
